// Public homepage for www.betbuilder.com.au (signed-out visitors).
// Brand: ink + mint, racehorse engraving, "we dig the stats".
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Login from './Login';
import { AuthUser } from '../api/auth';
import { api } from '../hub/hubApi';
import './Home.css';

interface HomeProps { onSuccess: (user: AuthUser) => void; }

const JOIN_MAIL = 'mailto:support@betbuilder.com.au?subject=Join%20Betbuilder%20(free)&body=Name%3A%0AHow%20I%20bet%20(tipsters%20I%20follow%2C%20tracks%2C%20bet%20size)%3A%0A';

const STEPS = [
  { n: '01', t: 'Join free', d: 'Sign up in a minute and set your unit size. Every tip and parlay is sized to your bank, not ours.' },
  { n: '02', t: 'Connect your Betfair', d: 'Link your own Betfair account. Your money never leaves it, and the bot only bets inside the limits you set.' },
  { n: '03', t: 'Bet your way', d: 'Let the bot follow our tips automatically, or build your own parlays across racing and sport, back or lay. Cash out whenever you like.' },
];

const HUB = [
  { k: 'Daily tips', to: '#signin', d: 'Our models dig through form, prices and market moves every day, so you start the card with the selections that matter.',
    icon: <><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.4 1 1.1 1 1.8V16h5v-.3c0-.7.4-1.4 1-1.8A6 6 0 0 0 12 3Z" /></> },
  { k: 'The bot', to: '#signin', d: 'Connect your Betfair account and the bot places the tips you choose, at your unit size, inside your limits. No screen-watching.',
    icon: <><rect x="5" y="7" width="14" height="12" rx="2" /><path d="M12 3v4M9 12h.01M15 12h.01M9 16h6M2 12h3M19 12h3" /></> },
  { k: 'Parlays, your way', to: '#signin', d: 'Combine any horse racing and sports legs into one parlay. Back or lay each leg, mix codes and build the combination you actually want.',
    icon: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></> },
  { k: 'Cash out any time', to: '#signin', d: 'Take a profit or cut a loss on any parlay at any point, not just when the last leg runs. You stay in control from first leg to last.',
    icon: <><path d="M12 3v12M7 10l5 5 5-5" /><path d="M4 17v3h16v-3" /></> },
];

const TICKER = ['Daily tips', 'Bot on your Betfair', 'Back or lay parlays', 'Racing and sport', 'Cash out any time', 'We dig the stats'];

const PIX = <img className="bbh-pix" src="/brand/pixel-horse.svg" alt="" width="34" height="20" />;

const pct = (v: any) => (v == null || !isFinite(v) ? '–' : (v * 100).toFixed(1) + '%');
const units = (v: any) => (v == null || !isFinite(v) ? '–' : (v > 0 ? '+' : '') + Number(v).toFixed(1));

const Home: React.FC<HomeProps> = ({ onSuccess }) => {
  const [board, setBoard] = useState<any[] | null>(null);
  const [live, setLive] = useState<any[]>([]);

  useEffect(() => {
    api('/leaderboard?division=ALL&window=all').then((d: any) => setBoard((d?.rows || []).filter((r: any) => r.ranked && !/^demo-/i.test(r.handle || '')).slice(0, 5))).catch(() => setBoard([]));
    api('/live').then((d: any) => setLive(Array.isArray(d) ? d : [])).catch(() => setLive([]));
  }, []);

  return (
    <div className="bb-home">
      <nav className="bbh-nav" aria-label="Main">
        <Link to="/" className="bbh-nav-logo"><img src="/brand/betbuilder-logo-white.png" alt="Betbuilder — we dig the stats" width="1100" height="149" /></Link>
        <div className="bbh-nav-links">
          <a href="#feed">The feed</a>
          <a href="#hub">Platform</a>
          <a href="#how">How it works</a>
          <Link to="/hub/leaderboard">Leaderboard</Link>
          <Link to="/hub/live">Live{live.length > 0 && <i className="bbh-dot" aria-label="someone is live" />}</Link>
        </div>
        <a className="bbh-btn bbh-btn-line bbh-nav-cta" href="#signin">Log in</a>
      </nav>

      <header className="bbh-hero">
        <div className="bbh-rail" aria-hidden="true" />
        <img className="bbh-horses" src="/brand/betbuilder-horses-white.png" alt="" aria-hidden="true" width="1400" height="393" />
        <div className="bbh-hero-inner">
          <div className="bbh-hero-copy">
            <p className="bbh-eyebrow">Racing and sports insights · Free to join</p>
            <h1>We dig<br />the <em>stats.</em></h1>
            <p className="bbh-lede">
              Betbuilder is the insights platform for Australian punters: our daily tips, a bot that bets them on your own
              Betfair account, and parlays built your way — back or lay, racing or sport, with cash out at any time.
            </p>
            <div className="bbh-cta-row">
              <a className="bbh-btn bbh-btn-mint" href={JOIN_MAIL}>Join free</a>
              <a className="bbh-btn bbh-btn-ghost" href="#how">How it works <span aria-hidden="true">→</span></a>
            </div>
            <dl className="bbh-facts">
              <div><dt>Tips</dt><dd>Every race day</dd></div>
              <div><dt>Bot</dt><dd>On your own Betfair</dd></div>
              <div><dt>Parlays</dt><dd>Cash out any time</dd></div>
            </dl>
          </div>
          <div className="bbh-signin" id="signin">
            <Login onSuccess={onSuccess} embedded />
          </div>
        </div>
      </header>

      <div className="bbh-ticker" aria-hidden="true">
        <div className="bbh-ticker-track">
          {[0, 1].map(k => <span key={k}>{TICKER.map(t => <React.Fragment key={t}>{t}<b>{PIX}</b></React.Fragment>)}</span>)}
        </div>
      </div>

      <section className="bbh-section bbh-ink bbh-feed" id="feed">
        <div className="bbh-wrap bbh-feed-wrap">
          <div className="bbh-section-head">
            <p className="bbh-kicker">Social, but sharp</p>
            <h2>A tipping feed with<br />the working shown.</h2>
            <p className="bbh-sub">Most betting apps give you a bet slip and a brag. Betbuilder gives you the pick, the reasoning behind it and the tipster's full record, so you know exactly what you're following before you copy it.</p>
            <ul className="bbh-points">
              <li><span>01</span><div><b>The pick</b> Race, runner, price, back or lay. Racing and sport, single or parlay.</div></li>
              <li><span>02</span><div><b>The why</b> Form, speed, track and market moves, in plain English.</div></li>
              <li><span>03</span><div><b>The record</b> Every tip settled in units. Losing runs included, nothing cherry-picked.</div></li>
            </ul>
          </div>
          <div className="bbh-cards" aria-label="Example feed cards">
            <article className="bbh-tip">
              <header><span className="bbh-av">BB</span><div><b>Betbuilder daily tip</b><small>Race 6 · 1200m · Soft 5</small></div><span className="bbh-tag">Example</span></header>
              <div className="bbh-leg"><span className="bbh-side back">Back</span><b>No. 4 · Example Runner</b><span className="bbh-odds">@ 6.40</span></div>
              <p className="bbh-why"><b>Why</b> Drawn to lead with no other speed in the race. Fastest last 600m of the field at its last two starts, and the market has firmed all morning.</p>
              <footer><button type="button" tabIndex={-1}>Send to my bot</button><button type="button" tabIndex={-1} className="ghost">Discuss</button></footer>
            </article>
            <article className="bbh-tip bbh-tip-2">
              <header><span className="bbh-av you">YOU</span><div><b>@your_handle</b><small>3-leg parlay · racing + NRL</small></div><span className="bbh-tag">Example</span></header>
              <div className="bbh-leg"><span className="bbh-side back">Back</span><b>R4 · No. 7</b><span className="bbh-odds">@ 3.20</span></div>
              <div className="bbh-leg"><span className="bbh-side back">Back</span><b>NRL · Home −4.5</b><span className="bbh-odds">@ 1.91</span></div>
              <div className="bbh-leg"><span className="bbh-side lay">Lay</span><b>R7 · No. 2</b><span className="bbh-odds">@ 2.60</span></div>
              <footer><span className="bbh-cash"><i />Cash out available</span><button type="button" tabIndex={-1} className="ghost">Copy</button></footer>
            </article>
          </div>
        </div>
      </section>

      <section className="bbh-section bbh-paper" id="how">
        <div className="bbh-wrap">
          <div className="bbh-section-head">
            <p className="bbh-kicker">How it works</p>
            <h2>Up and running<br />in three steps.</h2>
          </div>
          <ol className="bbh-steps">
            {STEPS.map(s => (
              <li key={s.n}><span className="bbh-step-n">{s.n}</span><h3>{s.t}</h3><p>{s.d}</p></li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bbh-section bbh-ink" id="hub">
        <div className="bbh-wrap">
          <div className="bbh-section-head">
            <p className="bbh-kicker">The platform</p>
            <h2>Tips, the bot and parlays.<br />All in one place.</h2>
          </div>
          <div className="bbh-grid">
            {HUB.map(h => (
              <Link key={h.k} to={h.to} className="bbh-card">
                <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{h.icon}</svg>
                <h3>{h.k}</h3>
                <p>{h.d}</p>
                <span className="bbh-card-go" aria-hidden="true">→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bbh-section bbh-paper">
        <div className="bbh-wrap bbh-board-wrap">
          <div className="bbh-section-head">
            <p className="bbh-kicker">Community</p>
            <h2>Punters who dig<br />the stats too.</h2>
            <p className="bbh-sub">Talk form on race day, watch members go live, and when you're ready, put your record on the leaderboard. Everyone is measured in units, on the same scale.</p>
            <Link className="bbh-btn bbh-btn-ink" to="/hub/leaderboard">Full leaderboard <span aria-hidden="true">→</span></Link>
          </div>
          <div className="bbh-board">
            <div className="bbh-board-head"><span>Top of the board</span><span className="bbh-mono">All · all time</span></div>
            {board === null ? <p className="bbh-board-empty">Loading…</p>
              : board.length === 0 ? (
                <div className="bbh-board-empty">
                  <p><strong>The board opens as records build.</strong></p>
                  <p>Leaders need enough settled picks before they are ranked, so a short lucky run can't top the table.</p>
                  <ul className="bbh-chips">{['Strike rate', 'Profit (units)', 'POT', 'Max drawdown', 'Worst run', 'Betbuilder Score'].map(c => <li key={c}>{c}</li>)}</ul>
                </div>
              ) : (
                <table>
                  <thead><tr><th>#</th><th>Leader</th><th className="r">Bets</th><th className="r">Strike</th><th className="r">Units</th><th className="r">POT</th></tr></thead>
                  <tbody>{board.map((r: any) => (
                    <tr key={r.handle}>
                      <td><span className={'bbh-rank r' + r.rank}>{r.rank}</span></td>
                      <td><Link to={'/hub/leaders/' + r.handle}>{r.displayName}</Link>{r.live && <span className="bbh-live">Live</span>}</td>
                      <td className="r">{r.bets}</td><td className="r">{pct(r.strikeRate)}</td>
                      <td className={'r ' + (r.profitUnits > 0 ? 'pos' : 'neg')}>{units(r.profitUnits)}</td>
                      <td className={'r ' + (r.levelPot > 0 ? 'pos' : 'neg')}>{pct(r.levelPot)}</td>
                    </tr>))}</tbody>
                </table>
              )}
          </div>
        </div>
      </section>

      <section className="bbh-final">
        <div className="bbh-wrap">
          <div className="bbh-race" aria-hidden="true"><img src="/brand/pixel-horse-ink.svg" alt="" width="68" height="40" /></div>
          <p className="bbh-press">Player 1 · Press start</p>
          <h2>Pull up a chair.</h2>
          <p>Free to join. Daily tips, the bot and parlays from day one. Founding members get in first and help shape what comes next.</p>
          <div className="bbh-cta-row center">
            <a className="bbh-btn bbh-btn-ink" href={JOIN_MAIL}>Join free</a>
            <a className="bbh-btn bbh-btn-ghost-ink" href="#signin">I have an account</a>
          </div>
        </div>
      </section>

      <footer className="bbh-footer">
        <div className="bbh-wrap bbh-footer-grid">
          <div>
            <img src="/brand/betbuilder-logo-white.png" alt="Betbuilder" width="1100" height="149" className="bbh-footer-logo" />
            <p className="bbh-fine">Betbuilder Pty Ltd · ABN 33 625 061 529<br /><a href="mailto:support@betbuilder.com.au">support@betbuilder.com.au</a></p>
          </div>
          <div className="bbh-footer-links">
            <Link to="/hub/leaderboard">Leaderboard</Link>
            <Link to="/hub/live">Live</Link>
            <a href="#how">How it works</a>
            <a href="#signin">Log in</a>
          </div>
          <div className="bbh-rg">
            <p className="bbh-rg-badge">18+</p>
            <p className="bbh-fine">
              Betbuilder is a racing and sports insights service, not a bookmaker. Bets are placed on your own Betfair account.
              For free, confidential support call Gambling Help Online on <a href="tel:1800858858">1800 858 858</a> or visit <a href="https://www.gamblinghelponline.org.au" target="_blank" rel="noreferrer">gamblinghelponline.org.au</a>.
              Self-exclude from all licensed wagering providers at <a href="https://www.betstop.gov.au" target="_blank" rel="noreferrer">betstop.gov.au</a>.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
